import React from 'react';
import { Card, StatusMark, TypeTag, ParallelGroup } from '../../components/ui';
import { computeLevels, numberSteps } from './taskGraph';

function CheckMarker() {
  return (
    <li className="flex justify-center">
      <span className="border-1.5 border-dashed border-ink rounded-full px-3 py-0.5 text-xs font-bold text-pencil">
        Check with me here
      </span>
    </li>
  );
}

function StepCard({ step, number, compact, selected, onStepClick, renderActions, tagsFor }) {
  const clickable = !!onStepClick;
  const tags = tagsFor ? tagsFor(step) : [];
  return (
    <Card
      as="article"
      variant={selected ? 'selected' : clickable ? 'interactive' : 'static'}
      aria-label={`Step ${number}: ${step.title}`}
      data-selected={selected ? 'true' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? (e) => onStepClick(step, e) : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onStepClick(step, e);
        }
      } : undefined}
      className={`relative p-3 pr-20 ${clickable ? 'focus-ring' : ''}`}
    >
      <div className="absolute top-2 right-2 flex items-center gap-2">
        {renderActions && renderActions(step)}
        <StatusMark status={step.status} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <TypeTag type={step.type} />
        <span className="font-sans font-bold text-ink">{step.title}</span>
        {tags.map((t) => (
          <span key={t} className="bg-eraser text-ink text-xs font-bold uppercase tracking-wider rounded px-1.5 py-0.5">{t}</span>
        ))}
      </div>
      {!compact && step.summary && <p className="mt-1 text-sm text-pencil">{step.summary}</p>}
    </Card>
  );
}

export default function StepGraph({
  graph, lead, compact = false, selectedKeys = [], onStepClick, renderActions, tagsFor,
}) {
  if (!graph || !graph.steps.length) {
    return <p className="text-pencil">No steps yet.</p>;
  }
  const levels = computeLevels(graph);
  const numbers = numberSteps(graph, levels);
  const byKey = Object.fromEntries(graph.steps.map((s) => [s.key, s]));

  const items = [];
  let lastWasMarker = false;
  const addMarker = () => {
    if (lastWasMarker) return;
    items.push(<CheckMarker key={`check-${items.length}`} />);
    lastWasMarker = true;
  };
  const card = (s) => (
    <StepCard
      key={s.key}
      step={s}
      number={numbers[s.key]}
      compact={compact}
      selected={selectedKeys.includes(s.key)}
      onStepClick={onStepClick}
      renderActions={renderActions}
      tagsFor={tagsFor}
    />
  );
  levels.forEach((level, i) => {
    const steps = level.map((k) => byKey[k]);
    const real = steps.filter((s) => !s.isCheck);
    if (real.length) {
      items.push(
        <li key={`level-${i}`}>
          {real.length === 1 ? card(real[0]) : (
            <ParallelGroup>
              <div className="flex gap-2 flex-wrap [&>*]:flex-1 [&>*]:min-w-[200px]">{real.map(card)}</div>
            </ParallelGroup>
          )}
        </li>,
      );
      lastWasMarker = false;
    }
    if (steps.some((s) => s.isCheck || s.checkAfter)) addMarker();
  });

  return (
    <ol className="flex flex-col gap-3 list-none p-0 m-0">
      {lead && <li>{lead}</li>}
      {items}
    </ol>
  );
}
